/** @jsx jsx */
import { Layer } from "@cd2/components/build/es6"
import { css, jsx } from "@emotion/core"
import { useCallback, useEffect, useRef } from "react"
import { IComponentProp } from "@cd2/tools"
import { explodePromise } from "../utils/explodePromise"
import { useTheme } from "@cd2/theme"

type ManagedDrawProps<Res, Data = void> = {
  dialogData: Data
  onResolve(response: Res): void
  registerOutsideClick: (func: () => void) => void
}

export function useManagedDraw<Res, Data = void>({
  width,
  Component,
  onlyDestroyOnResolve,
}: {
  width?: IComponentProp<typeof Layer, "theme">["layerWidth"]
  Component(props: ManagedDrawProps<Res, Data>): JSX.Element
  // If true, the modal will only be destroyed when the promise is resolved
  // this is needed if you want to remove the underlying component and keep the modal
  // for example if you want to trigger a modal from a tooltip.
  onlyDestroyOnResolve?: boolean
}) {
  const outsideClickCbRef = useRef<(() => void) | null>(null)
  function registerOutsideClick(func: () => void) {
    outsideClickCbRef.current = func
  }

  const Node = useCallback(
    ({ showDialog, data, onResolve }) => (
      <Drawer
        isOpen={showDialog}
        onRequestClose={() => {
          outsideClickCbRef.current?.()
        }}
        side="right"
        width={(width ?? 480) as any}
      >
        {showDialog && (
          <div
            css={css`
              padding: 64px 32px;
              height: 100vh;
              overflow: auto;
            `}
          >
            <Component
              dialogData={data}
              onResolve={onResolve}
              registerOutsideClick={registerOutsideClick}
            />
          </div>
        )}
      </Drawer>
    ),
    [width],
  )

  const modalRef = useRef<any>(null)

  useEffect(() => {
    modalRef.current = window.MANAGED_MODALS.create()

    return () => {
      if (!onlyDestroyOnResolve) {
        modalRef.current.destroy()
      }
    }
  }, [])

  const openModal = (data: Data) => {
    const exploded = explodePromise<Res>()

    const onResolve = (response: Res) => exploded.resolve(response)
    // show the modal
    modalRef.current.setComponent(Node, { showDialog: false, data, onResolve })
    setTimeout(() => modalRef.current.setProps({ showDialog: true, data, onResolve }), 10)
    exploded.promise.then(() => {
      modalRef.current.setProps({ showDialog: false })
      setTimeout(() => modalRef.current.setComponent(() => null, {}), 300)
    })

    return exploded.promise
  }

  return openModal
}

function Drawer({
  children,
  isOpen,
  onRequestClose,
  width,
  side,
}: {
  children: React.ReactNode
  isOpen: boolean
  onRequestClose: () => void
  width: number
  side: "left" | "right"
}) {
  const layer = useTheme().layer
  return (
    <Layer
      wrapperClassName="layerWrapperClassName"
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      theme={{
        animation: side === "left" ? "slideLeft" : "slideRight",
        layerWidth: width ?? layer.drawer?.width ?? 300,
        position: "left",
        layerColor: "white",
        height: "100%",
      }}
    >
      <style>
        {`
          .layerWrapperClassName > div {
            max-width: 95vw;
          }
        `}
      </style>
      {children}
    </Layer>
  )
}
